/**
 * noga-2025.data.ts - an OFFLINE, static NOGA-2025 (BFS "Allgemeine Systematik der
 * Wirtschaftszweige") code -> German-label catalog.
 *
 * WHY THIS EXISTS
 * §3.1 of the Part-2 spec: our existing enrichment (UID PublicServices SOAP) already returns
 * a NOGA code per firm for free (`RegisterEnrichmentResult.noga`, e.g. `'62010'`) but has
 * nowhere to turn it into a human-readable "Branche" label. Rather than calling the BFS KUBB
 * tool (kubb-tool.bfs.admin.ch) or Zenyio (zenyio.ch/de/noga-finder) at runtime - an external
 * dependency, rate limits, availability risk, and redundant since we already have the code -
 * we resolve it OFFLINE against a bundled table, same pattern as `first-name-gender.data.ts`.
 * KUBB + Zenyio were used ONCE, by us, to look up/cross-check the labels below; neither is
 * ever called from application code.
 *
 * TWO-TIER TABLE (a deliberate, documented scope decision - see SCOPE below)
 *  - `NOGA_2025_LABELS`  - exact 5-digit CLASS codes (Swiss format: 4-digit NACE Rev.2 class +
 *    a national digit, e.g. `62010` = NACE 62.01 + '0' - the exact shape the fixture/enrichment
 *    data already uses, see `apps/api/src/modules/enrichment/fixture-uid-registry.provider.ts`).
 *    A CURATED SUBSET (~70 entries) covering the sectors a Swiss insurance broker's book
 *    actually contains (Bau, Handel, Gastgewerbe, IT, Finanzen/Versicherung, Beratung,
 *    Gesundheit, Immobilien, Transport, Produktion/Gewerbe) - NOT the full official
 *    ~1'845-class BFS table (that table sits behind licensed/paid distribution today; see
 *    SCOPE below for how to extend it safely).
 *  - `NOGA_DIVISION_LABELS` - the FULL, verified 2-digit DIVISION table (all 88 real NACE
 *    Rev.2 divisions NOGA-2025 uses; the numbering has known gaps - 04, 34, 40, 44, 45, 48,
 *    54, 57, 67, 76, 83, 89 do not exist - which `resolveBranche` (./noga.ts) relies on to
 *    tell a genuinely unknown code from a real one it just doesn't have a class label for
 *    yet). Sourced 2026-08-20 from the public NOGA-2025 division listing (openswissdata.com/
 *    de/codes/, itself built on the BFS NOGA-2025 structure published 2025-01) and
 *    cross-checked against the stable NACE Rev.2 division numbering.
 *
 * `resolveBranche(nogaCode)` (./noga.ts) tries the exact 5-digit class first, then falls back
 * to the 2-digit division - so EVERY real NOGA code returns a sensible German Branche label
 * today, not just the curated ~70. Only a code whose 2-digit prefix isn't a real division
 * (or empty/garbage input) resolves to `null` - the "unknown code -> null" contract.
 *
 * SCOPE / HOW TO EXTEND
 * This is intentionally NOT the full ~1'845-code table (the spec's "~700 codes" figure is an
 * order-of-magnitude estimate for the exact-class tier; the full BFS distribution is a paid
 * dataset, e.g. openswissdata.com's "Classifications dataset"). Grow `NOGA_2025_LABELS`
 * incrementally as real customer `noga_code` values surface in production that only resolve
 * at division granularity today - the resolver logic never has to change (same growth model
 * as `first-name-gender.data.ts`). Flagged for Valerio: if 5-digit-exact parity across the
 * full official table is required before shipping to production, that needs either a licensed
 * data import or a one-time manual KUBB/Zenyio pass to backfill this file - out of scope here.
 *
 * [Source: projects/broker-ag/dms/spec-part2-stefan-delta.md §3.1, §3.2, P2-6]
 * [Source: apps/api/src/modules/enrichment/fixture-uid-registry.provider.ts] (noga: '62010' = "Programmierungstätigkeiten")
 * [Source: apps/api/src/modules/enrichment/uid-public-services.provider.ts#L232] (noga code format)
 * [Source: apps/api/src/modules/mandate/first-name-gender.data.ts] (offline-static-table precedent)
 */

/**
 * NOGA_2025_LABELS - exact 5-digit NOGA-2025 class code -> German Branche label.
 * Curated subset (SCOPE above). Keys are 5-digit numeric strings (no dots), matching
 * `firma.noga_code` / `RegisterEnrichmentResult.noga` exactly.
 */
export const NOGA_2025_LABELS: Readonly<Record<string, string>> = {
  // ── A · Land- und Forstwirtschaft ──────────────────────────────────────────
  '01500': 'Gemischte Landwirtschaft',
  '01610': 'Erbringung von landwirtschaftlichen Dienstleistungen',
  '02100': 'Forstwirtschaft',

  // ── C · Verarbeitendes Gewerbe / Herstellung ───────────────────────────────
  '10710': 'Herstellung von Backwaren (ohne Dauerbackwaren)',
  '16290': 'Herstellung von sonstigen Holz-, Kork-, Flecht- und Korbwaren (ohne Möbel)',
  '18120': 'Sonstiger Druck',
  '25620': 'Mechanik a. n. g.',
  '28290': 'Herstellung von sonstigen nicht wirtschaftszweigspezifischen Maschinen a. n. g.',
  '31090': 'Herstellung von sonstigen Möbeln',
  '33120': 'Reparatur von Maschinen',
  '33200': 'Installation von Maschinen und Ausrüstungen a. n. g.',

  // ── F · Baugewerbe ──────────────────────────────────────────────────────────
  '41100': 'Erschliessung von Bauträgergrundstücken',
  '41200': 'Bau von Gebäuden',
  '42110': 'Bau von Strassen',
  '42990': 'Sonstiger Tiefbau a. n. g.',
  '43110': 'Abbrucharbeiten',
  '43120': 'Bauvorbereitung',
  '43210': 'Elektroinstallation',
  '43220': 'Gas-, Wasser-, Heizungs- und Klimainstallation',
  '43290': 'Sonstige Bauinstallation',
  '43310': 'Stuckateurgewerbe',
  '43320': 'Bautischlerei und -schlosserei',
  '43330': 'Fussboden-, Fliesen- und Wandverkleidungsgewerbe',
  '43341': 'Malerei',
  '43390': 'Sonstiger Ausbau a. n. g.',
  '43910': 'Dachdeckerei und Zimmerei',
  '43990': 'Sonstiges spezialisiertes Baugewerbe a. n. g.',

  // ── G · Handel; Instandhaltung/Reparatur von Kraftfahrzeugen ───────────────
  '45200': 'Instandhaltung und Reparatur von Kraftfahrzeugen',
  '46190': 'Handelsvermittlung von Waren allgemeiner Art',
  '47110': 'Einzelhandel mit Waren verschiedener Art (Hauptrichtung Nahrungsmittel)',
  '47190': 'Sonstiger Einzelhandel in Verkaufsräumen (Waren verschiedener Art)',
  '47710': 'Einzelhandel mit Bekleidung',
  '47910': 'Versandhandel',

  // ── H · Verkehr und Lagerei ─────────────────────────────────────────────────
  '49410': 'Güterbeförderung im Strassenverkehr',
  '52290': 'Sonstige Erbringung von Dienstleistungen für den Verkehr',

  // ── I · Gastgewerbe/Beherbergung ────────────────────────────────────────────
  '55100': 'Hotels, Gasthöfe und Pensionen',
  '56100': 'Restaurants',
  '56300': 'Bars',

  // ── J · Information und Kommunikation ───────────────────────────────────────
  '58290': 'Sonstiger Verlag von Software',
  '62010': 'Programmierungstätigkeiten',
  '62020': 'Erbringung von Beratungsleistungen auf dem Gebiet der Informationstechnologie',
  '62030': 'Betrieb von Datenverarbeitungseinrichtungen für Dritte',
  '62090': 'Erbringung von sonstigen Dienstleistungen der Informationstechnologie',
  '63110': 'Datenverarbeitung, Hosting u. Ä.',
  '63120': 'Webportale',
  '63990': 'Erbringung von sonstigen Informationsdienstleistungen a. n. g.',

  // ── K · Erbringung von Finanz- und Versicherungsdienstleistungen ───────────
  '64200': 'Beteiligungsgesellschaften',
  '64300': 'Fonds und ähnliche Finanzinstitutionen',
  '64990': 'Sonstige Finanzdienstleistungen a. n. g.',
  '65110': 'Lebensversicherungen',
  '65120': 'Nichtlebensversicherungen (Schadenversicherung)',
  '65200': 'Rückversicherungen',
  '65300': 'Pensionskassen',
  '66190': 'Sonstige mit Finanzdienstleistungen verbundene Tätigkeiten',
  '66220': 'Tätigkeit von Versicherungsagenturen und -maklern',
  '66290': 'Sonstige mit Versicherungsdienstleistungen und Pensionskassen verbundene Tätigkeiten',

  // ── L · Grundstücks- und Wohnungswesen ───────────────────────────────────────
  '68100': 'Kauf und Verkauf von eigenen Grundstücken, Gebäuden und Wohnungen',
  '68200': 'Vermietung, Verpachtung von eigenen oder geleasten Grundstücken, Gebäuden und Wohnungen',
  '68320': 'Verwaltung von Grundstücken, Gebäuden und Wohnungen für Dritte',

  // ── M · Freiberufliche, wissenschaftliche und technische Dienstleistungen ──
  '69100': 'Rechtsberatung',
  '69200': 'Wirtschaftsprüfung und Steuerberatung; Buchführung',
  '70100': 'Verwaltung und Führung von Unternehmen und Betrieben',
  '70210': 'Public-Relations-Beratung',
  '70220': 'Unternehmensberatung',
  '71110': 'Architekturbüros',
  '71120': 'Ingenieurbüros sowie damit verbundene technische Beratung',
  '71200': 'Technische, physikalische und chemische Untersuchung',
  '73110': 'Werbeagenturen',
  '74100': 'Ateliers für Textil-, Schmuck-, Grafik- und sonstiges Design',

  // ── N · Erbringung von wirtschaftlichen Dienstleistungen für Unternehmen/Private ─
  '77320': 'Vermietung von Baumaschinen und -geräten',
  '78100': 'Vermittlung von Arbeitskräften',
  '81210': 'Allgemeine Gebäudereinigung',
  '81300': 'Garten- und Landschaftsbau',
  '82990': 'Erbringung von sonstigen wirtschaftlichen Dienstleistungen a. n. g.',

  // ── P/Q · Erziehung/Gesundheits- und Sozialwesen ─────────────────────────────
  '85590': 'Sonstiger Unterricht a. n. g.',
  '86210': 'Arztpraxen für Allgemeinmedizin',
  '86220': 'Facharztpraxen',
  '86230': 'Zahnarztpraxen',
  '86900': 'Sonstiges Gesundheitswesen',
  '87100': 'Pflegeheime',
  '88100': 'Sozialwesen (ohne Heime) für ältere Menschen und Behinderte',
  '88910': 'Kinderbetreuung',

  // ── R/S · Kunst/Unterhaltung, sonstige Dienstleistungen ─────────────────────
  '93130': 'Fitnesszentren',
  '95110': 'Reparatur von Datenverarbeitungsgeräten und peripheren Geräten',
  '96020': 'Frisör- und Kosmetiksalons',
} as const;

/**
 * NOGA_DIVISION_LABELS - the full 2-digit NOGA-2025 DIVISION code -> German label table (all
 * 88 real divisions; gaps in the numbering below - 04, 34, 40, 44, 45, 48, 54, 57, 67, 76, 83,
 * 89 - are NOT missing data, they simply are not valid NACE Rev.2/NOGA-2025 division codes).
 * The `resolveBranche` fallback tier - see file header.
 */
export const NOGA_DIVISION_LABELS: Readonly<Record<string, string>> = {
  '01': 'Landwirtschaft, Jagd und damit verbundene Tätigkeiten',
  '02': 'Forstwirtschaft und Holzeinschlag',
  '03': 'Fischerei und Aquakultur',
  '05': 'Kohlenbergbau',
  '06': 'Gewinnung von Erdöl und Erdgas',
  '07': 'Erzbergbau',
  '08': 'Gewinnung von Steinen und Erden, sonstiger Bergbau',
  '09': 'Erbringung von Dienstleistungen für den Bergbau und für die Gewinnung von Steinen und Erden',
  '10': 'Herstellung von Nahrungs- und Futtermitteln',
  '11': 'Getränkeherstellung',
  '12': 'Tabakverarbeitung',
  '13': 'Herstellung von Textilien',
  '14': 'Herstellung von Bekleidung',
  '15': 'Herstellung von Leder, Lederwaren und ähnlichen Produkten aus anderen Materialien',
  '16': 'Herstellung von Holz-, Flecht-, Korb- und Korkwaren, ohne Möbel',
  '17': 'Herstellung von Papier, Pappe und Waren daraus',
  '18': 'Herstellung von Druckerzeugnissen; Vervielfältigung von bespielten Ton-, Bild- und Datenträgern',
  '19': 'Kokerei und Mineralölverarbeitung',
  '20': 'Herstellung von chemischen Erzeugnissen',
  '21': 'Herstellung von pharmazeutischen Erzeugnissen',
  '22': 'Herstellung von Gummi- und Kunststoffwaren',
  '23': 'Herstellung von Glas und Glaswaren, Keramik, Verarbeitung von Steinen und Erden',
  '24': 'Metallerzeugung und -bearbeitung',
  '25': 'Herstellung von Metallerzeugnissen',
  '26': 'Herstellung von Datenverarbeitungsgeräten, elektronischen und optischen Erzeugnissen',
  '27': 'Herstellung von elektrischen Ausrüstungen',
  '28': 'Maschinenbau a. n. g.',
  '29': 'Herstellung von Kraftwagen und Kraftwagenteilen',
  '30': 'Sonstiger Fahrzeugbau',
  '31': 'Herstellung von Möbeln',
  '32': 'Herstellung von sonstigen Waren',
  '33': 'Reparatur, Instandhaltung und Installation von Maschinen und Ausrüstungen',
  '35': 'Energieversorgung',
  '36': 'Wasserversorgung',
  '37': 'Abwasserentsorgung',
  '38': 'Sammlung, Verwertung und Beseitigung von Abfällen',
  '39': 'Beseitigung von Umweltverschmutzungen und sonstige Tätigkeiten in der Abfallbewirtschaftung',
  '41': 'Hochbau',
  '42': 'Tiefbau',
  '43': 'Vorbereitende Baustellenarbeiten, Bauinstallation und sonstiges Ausbaugewerbe',
  '46': 'Grosshandel',
  '47': 'Einzelhandel',
  '49': 'Landverkehr und Transport in Rohrfernleitungen',
  '50': 'Schifffahrt',
  '51': 'Luftfahrt',
  '52': 'Lagerei sowie Erbringung von sonstigen Dienstleistungen für den Verkehr',
  '53': 'Post-, Kurier- und Expressdienste',
  '55': 'Beherbergung',
  '56': 'Gastronomie',
  '58': 'Verlagswesen',
  '59': 'Herstellung, Verleih und Vertrieb von Filmen und Fernsehprogrammen; Kinos; Tonstudios und Verlegen von Musik',
  '60': 'Rundfunkveranstalter, Nachrichtenagenturen und sonstige Verbreitung von Medieninhalten',
  '61': 'Telekommunikation',
  '62': 'Erbringung von Dienstleistungen der Informationstechnologie',
  '63': 'Datenverarbeitung, Hosting und Erbringung sonstiger Informationsdienstleistungen',
  '64': 'Erbringung von Finanzdienstleistungen',
  '65': 'Versicherungen, Rückversicherungen und Pensionskassen, ohne Sozialversicherung',
  '66': 'Mit Finanz- und Versicherungsdienstleistungen verbundene Tätigkeiten',
  '68': 'Grundstücks- und Wohnungswesen',
  '69': 'Rechts- und Steuerberatung, Wirtschaftsprüfung',
  '70': 'Verwaltung und Führung von Unternehmen und Betrieben; Unternehmensberatung',
  '71': 'Tätigkeiten von Architektur- und Ingenieurbüros; technische, physikalische und chemische Untersuchung',
  '72': 'Forschung und Entwicklung',
  '73': 'Werbung und Marktforschung sowie Public-Relations-Beratung',
  '74': 'Sonstige wissenschaftliche und technische Tätigkeiten',
  '75': 'Veterinärwesen',
  '77': 'Vermietung und Leasing',
  '78': 'Vermittlung und Überlassung von Arbeitskräften',
  '79': 'Reisebüros, Reiseveranstalter und Erbringung sonstiger Reservierungsdienstleistungen',
  '80': 'Wach- und Sicherheitsdienste sowie Detekteien',
  '81': 'Gebäudebetreuung; Garten- und Landschaftsbau',
  '82': 'Erbringung von wirtschaftlichen Dienstleistungen für Unternehmen und Privatpersonen',
  '84': 'Öffentliche Verwaltung, Verteidigung; Sozialversicherung',
  '85': 'Erziehung und Unterricht',
  '86': 'Gesundheitswesen',
  '87': 'Heime, ohne Erholungs- und Ferienheime',
  '88': 'Sozialwesen, ohne Heime',
  '90': 'Kunstschaffende Tätigkeiten und Tätigkeiten in der darstellenden Kunst',
  '91': 'Bibliotheken, Archive, Museen, botanische und zoologische Gärten',
  '92': 'Spiel-, Wett- und Lotteriewesen',
  '93': 'Erbringung von Dienstleistungen des Sports, der Unterhaltung und der Erholung',
  '94': 'Interessenvertretungen sowie kirchliche und sonstige religiöse Vereinigungen, ohne Sozialwesen und Sport',
  '95': 'Reparatur und Instandhaltung von Datenverarbeitungsgeräten und Gebrauchsgütern sowie von Kraftwagen und Krafträdern',
  '96': 'Erbringung von überwiegend persönlichen Dienstleistungen',
  '97': 'Private Haushalte mit Hauspersonal',
  '98': 'Herstellung von Waren und Erbringung von Dienstleistungen durch private Haushalte für den Eigenbedarf ohne ausgeprägten Schwerpunkt',
  '99': 'Exterritoriale Organisationen und Körperschaften',
} as const;
